import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  getRoleAccessEvents,
  updateEventRoleAccess,
} from "../../server/Api";
import {
  EVENT_ACCESS_MODULES,
  groupAccessModules,
} from "../../utils/eventAccessModules";
import "../../assets/css/occasion.css";
import "../../assets/css/RoleAccess.css";

const RoleAccess = () => {
  const user = useSelector((state) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [draftPages, setDraftPages] = useState(
    EVENT_ACCESS_MODULES.map((m) => m.key)
  );

  const moduleGroups = useMemo(() => groupAccessModules(), []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(getRoleAccessEvents());
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setEvents(list);
      setSelectedEventId((prev) => {
        if (prev && list.some((e) => String(e.eventId) === String(prev))) {
          return prev;
        }
        return list[0]?.eventId || null;
      });
      if (!list.length) {
        setLoadError("No events found. Create an event in Event Master first.");
      }
    } catch (error) {
      console.error("Failed to load role access:", error);
      const message =
        error.response?.data?.message || "Failed to load role access";
      setLoadError(message);
      setEvents([]);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.isSuperAdmin) loadEvents();
  }, [user?.isSuperAdmin, loadEvents]);

  const selectedEvent = useMemo(
    () =>
      events.find((e) => String(e.eventId) === String(selectedEventId)) || null,
    [events, selectedEventId]
  );

  useEffect(() => {
    if (!selectedEvent) {
      setDraftPages(EVENT_ACCESS_MODULES.map((m) => m.key));
      return;
    }
    const pages = Array.isArray(selectedEvent.allowedPages)
      ? selectedEvent.allowedPages
      : EVENT_ACCESS_MODULES.map((m) => m.key);
    setDraftPages([...pages]);
  }, [selectedEvent]);

  const filteredEvents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return events;
    return events.filter((event) => {
      const adminText = `${event.adminUser?.name || ""} ${
        event.adminUser?.email || ""
      }`.toLowerCase();
      return (
        String(event.eventName || "")
          .toLowerCase()
          .includes(q) ||
        String(event.eventCode || "")
          .toLowerCase()
          .includes(q) ||
        adminText.includes(q)
      );
    });
  }, [events, searchTerm]);

  const togglePage = (key) => {
    const module = EVENT_ACCESS_MODULES.find((m) => m.key === key);
    if (module?.alwaysOn) return;
    setDraftPages((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const selectAll = () => {
    setDraftPages(EVENT_ACCESS_MODULES.map((m) => m.key));
  };

  const selectEssential = () => {
    setDraftPages([
      "dashboard",
      "guest-master",
      "guest-attendee",
      "allocate-guest",
      "occasion-master",
    ]);
  };

  const handleSave = async () => {
    if (!selectedEventId) {
      toast.error("Select an event first");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(updateEventRoleAccess(selectedEventId), {
        allowedPages: draftPages,
      });
      const nextPages = res.data?.data?.allowedPages || draftPages;
      setEvents((prev) =>
        prev.map((event) =>
          String(event.eventId) === String(selectedEventId)
            ? { ...event, allowedPages: nextPages, hasCustomAccess: true }
            : event
        )
      );
      setDraftPages(nextPages);
      toast.success("Page access saved");
    } catch (error) {
      console.error("Failed to save role access:", error);
      toast.error(
        error.response?.data?.message || "Failed to save page access"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!user?.isSuperAdmin) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="container-fluid role-access-page">
      <div className="occasion-top row g-4 align-items-center">
        <div className="col-lg-9">
          <h2 className="mb-0">Role Access</h2>
        </div>
        <div className="col-lg-3">
          <input
            type="text"
            className="form-control"
            placeholder="Search event or admin…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <p className="role-access-loading">Loading events…</p>
      ) : (
        <div className="role-access-layout">
          <aside className="role-access-event-list" aria-label="Events">
            <h3 className="role-access-section-title">Events</h3>
            {filteredEvents.length ? (
              filteredEvents.map((event) => {
                const isSelected =
                  String(event.eventId) === String(selectedEventId);
                return (
                  <button
                    key={String(event.eventId)}
                    type="button"
                    className={`role-access-event-card${
                      isSelected ? " is-selected" : ""
                    }`}
                    onClick={() => setSelectedEventId(event.eventId)}
                  >
                    <strong>{event.eventName}</strong>
                    <span>
                      {event.adminUser?.email || "No event admin linked"}
                    </span>
                    <em>
                      {event.isEventActive ? "Active" : "Inactive"} ·{" "}
                      {(event.allowedPages || []).length} pages
                    </em>
                  </button>
                );
              })
            ) : (
              <p className="role-access-empty">
                {loadError || "No events match your search."}
              </p>
            )}
          </aside>

          <section className="role-access-panel">
            <header className="role-access-panel-head">
              <div>
                <h3>
                  {selectedEvent ? selectedEvent.eventName : "Page access"}
                </h3>
                <p>
                  {selectedEvent
                    ? `Event Admin: ${
                        selectedEvent.adminUser
                          ? `${selectedEvent.adminUser.name || "—"} (${
                              selectedEvent.adminUser.email
                            })`
                          : "Not linked"
                      }`
                    : "Select an event on the left to edit access"}
                </p>
              </div>
              <div className="role-access-panel-actions">
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={selectEssential}
                  disabled={!selectedEvent}
                >
                  Essential
                </button>
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={selectAll}
                  disabled={!selectedEvent}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="btn btn-save"
                  onClick={handleSave}
                  disabled={saving || !selectedEvent}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </header>

            <div className="role-access-groups">
              {moduleGroups.map((group) => (
                <div className="role-access-group" key={group.group}>
                  <h4 className="role-access-pages-heading">{group.group}</h4>
                  <ul className="role-access-page-list">
                    {group.items.map((module) => {
                      const checked = draftPages.includes(module.key);
                      const locked = Boolean(module.alwaysOn);
                      return (
                        <li key={module.key}>
                          <label
                            className={`role-access-module${
                              checked ? " is-on" : ""
                            }${locked ? " is-locked" : ""}`}
                          >
                            <input
                              type="checkbox"
                              className="role-access-checkbox"
                              checked={checked}
                              disabled={locked || !selectedEvent}
                              onChange={() => togglePage(module.key)}
                            />
                            <span className="role-access-module-copy">
                              <strong>{module.label}</strong>
                              {locked ? <em>Always on</em> : null}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default RoleAccess;
